import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { toast } from 'react-toastify';
import { useAccount } from '../../hooks/use-account';

interface NDConnectSSHReadyProps {
  handleClose: () => void;
  data: any;
  showReadyDialog: boolean;
}

export const NDConnectSSHReady = ({ handleClose, data, showReadyDialog }: NDConnectSSHReadyProps) => {
  const { storageToken } = useAccount();

  // The download endpoint now requires authentication (OOB authorization), so
  // we fetch it with the Bearer token and save the blob rather than opening a
  // plain link (which would not carry the Authorization header -> 401).
  const handleDownloadKey = async () => {
    const fileName = data?.jumpbox_private_key_file_name;
    if (!fileName) return;
    try {
      const session = storageToken();
      const response = await fetch(
        `${import.meta.env.VITE_API}/download-raw-ssh-private-key/${fileName}`,
        { headers: { Authorization: `Bearer ${session?.access_token}` } },
      );
      if (!response.ok) {
        let detail = `Error ${response.status}`;
        try {
          detail = (await response.json())?.detail ?? detail;
        } catch {
          // response body was not JSON; keep the status-based message
        }
        toast.error(`Failed to download jump box key: ${detail}`);
        return;
      }
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      toast.error(`Failed to download jump box key: ${err}`);
    }
  };

  return (
    <>
      <Dialog
        open={showReadyDialog}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '18px' }}>SSH Access Ready</DialogTitle>
        <DialogContent>
          {data ? (
            <>
              <Typography variant="h5" sx={{ mb: 5, color: 'red' }}>
                [Download Jump Box KEY]
              </Typography>
              <Button
                variant="outlined"
                onClick={handleDownloadKey}
                endIcon={<DownloadIcon fontSize="small" />}
              >
                Download
              </Button>
              <Typography variant="h5" sx={{ mt: 5, mb: 5, color: 'red' }}>
                [SSH and SCP Commands]
              </Typography>
              <Typography sx={{ wordBreak: 'break-all', color: 'primary.main' }}>
                Instructions to connect via SSH or transfer files using SCP:
              </Typography>
              <Typography sx={{ wordBreak: 'break-all', color: 'primary.main', mt: 2 }}>
                SSH (username & password):<br/>
                {data.ssh_command_password}
              </Typography>
              <Typography sx={{ wordBreak: 'break-all', color: 'primary.main', mt: 2 }}>
                SCP (username & password):<br/>
                {data.scp_command_password}
              </Typography>
              <Typography sx={{ wordBreak: 'break-all', color: 'primary.main', mt: 2 }}>
                SSH (using target key file):<br/>
                {data.ssh_command_key}
              </Typography>
              <Typography sx={{ wordBreak: 'break-all', color: 'primary.main', mt: 2 }}>
                SCP (using target key file):<br/>
                {data.scp_command_key}
              </Typography>
              <Typography variant="h5" sx={{ mt: 5, mb: 5, color: 'red' }}>
                [Usage]
              </Typography>
              <Typography sx={{ wordBreak: 'break-all', color: 'primary.main' }}>
                {data.usage.split('\n').map((e: string) => <>{e}<br/></>)}
              </Typography>
            </>
          ) : (
            <Typography>No URL returned from server.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
